<template>
	<div id="gestionEnt">
		<!-- en-tête nom et logo entreprise  -->
		<div class="sm:flex items-center m-auto text-center sm:text-left">
			<div class="w-full sm:w-1/2 flex mt-auto">
				<vs-button
					color="#2c2c2c"
					type="flat"
					icon-pack="feather"
					icon="icon-arrow-left"
					class="inline"
					@click="$router.push({ path: '/', query: $route.query })"
				/>

				<h2 class="inline ml-2" style="line-height: 35px">{{ $t("Membres") }}</h2>
			</div>
			<div v-if="organization" class="w-full sm:w-1/2 flex">
				<img
					v-show="getImageUrl(organization)"
					:src="getImageUrl(organization)"
					class="rounded-full shadow-md ml-auto"
					height="50px"
					width="50px"
					style="object-fit: contain"
				/>
				<h2 class="title-sinao mt-auto mb-auto ml-4 mr-4">
					{{ organization.name }}
				</h2>
			</div>
		</div>

		<!-- Card principale -->
		<vx-card
			v-if="(app?.admin_user_id === user.id && subscription) || user.is_superadmin"
			class="mt-4 text-center shadow-none border"
		>
			<h4 class>{{ $t("Gestion des membres") }}</h4>
			<p class="paragraph">
				{{
					$t("Ajoutez ici les membres de votre organisation...", {
						nickname: $t(subscription.plan.name) || subscription.plan.nickname.toUpperCase() || "",
						limit: subscription.plan.limits.users > 0 ? subscription.plan.limits.users : "∞"
					})
				}}
				<br />
				{{
					$t("Vous avez actuellement {count} utilisateur(s).", {
						count: (members.length || 0) + (invites.length || 0)
					})
				}}
			</p>

			<p v-if="subscription.plan && !subscription.plan.hasOwnProperty('associated_license_plan')" class="mb-5">
				{{ $t("Les utilisateurs supplémentaires sont facturés 10€ / utilisateur.") }}
			</p>

			<s-button :to="{ name: 'Invite', params: { appId } }" icon="icon-user-plus" type="success">
				{{ $t("Ajouter un nouveau membre") }}
			</s-button>

			<h4 class="mb-5 pt-5">{{ $t("Membres") }}</h4>

			<!-- Membres -->
			<vs-table
				v-if="members && members.length > 0"
				ref="tableUsers"
				class="text-left relative"
				stripe
				:data="[...members, ...supports]"
				hover-flat
			>
				<template slot="thead">
					<vs-th />
					<vs-th>{{ $t("Nom") }}</vs-th>
					<vs-th>{{ $t("Droits") }}</vs-th>
					<vs-th>{{ $t("Derniere Connexion") }}</vs-th>
					<vs-th>{{ $t("Actions") }}</vs-th>
				</template>

				<template slot-scope="{ data }">
					<vs-tr v-for="(member, index) in data" :key="index">
						<!-- Colonne 1 : photo avec gestion si admin -->
						<Fragment v-if="member && member.user">
							<vs-td class="photoCol">
								<vs-tooltip
									v-if="index === 0"
									:text="adminTooltip.text"
									:title="adminTooltip.title"
									position="top"
								>
									<vs-icon
										v-if="index === 0"
										class="absolute"
										icon="star"
										color="var(--warning)"
										size="1.5rem"
									/>
									<img
										:src="getImageUrl(member.user)"
										class="rounded-full img-profile"
										height="50px"
										width="50px"
										alt
									/>
								</vs-tooltip>
								<img
									v-else
									:src="getImageUrl(member.user)"
									class="rounded-full img-profile"
									height="50px"
									width="50px"
									alt
								/>
							</vs-td>

							<!-- Colonne 2 : prénom + nom -->
							<vs-td>
								<vs-tooltip
									v-if="index === 0"
									:text="adminTooltip.text"
									:title="adminTooltip.title"
									position="top"
								>
									{{ member.user.firstname }} {{ member.user.lastname }}
									<div style="font-size: 12px; color: var(--black)">{{ member.user.email }}</div>
								</vs-tooltip>
								<div v-else>
									{{ member.user.firstname }} {{ member.user.lastname }}
									<div style="font-size: 12px; color: var(--black)">{{ member.user.email }}</div>
								</div>
							</vs-td>

							<!-- Colonne 3 : Droits cliquable qui ouvre la showPoliciesPopups -->
							<vs-td class="w-1/3">
								<vs-tooltip
									v-if="member.user_id === app.admin_user_id"
									:text="adminTooltip.text"
									:title="adminTooltip.title"
									position="top"
								>
									<div>
										<p>{{ $t("Administrateur") }}</p>
									</div>
								</vs-tooltip>

								<div v-else-if="getPolicyById(member.policy_profile_id)?.visible == false">
									<p>{{ $t(getPolicyById(member.policy_profile_id)?.name ?? "") }}</p>
								</div>

								<div
									v-else
									class="align-vertical text-link hover:title-sinao cursor-pointer w-full pt-3 pb-3 no-underline hover:underline text-base"
									@click="openPoliciesPopup(member)"
								>
									<!-- {{ member }} -->
									<p v-if="allPolicies && member.policy_profile_id">
										{{ $t(getPolicyById(member.policy_profile_id)?.name ?? "") }}
									</p>
								</div>
							</vs-td>

							<vs-td>
								<vs-tooltip
									v-if="index === 0"
									:text="adminTooltip.text"
									:title="adminTooltip.title"
									position="top"
								>
									{{ toDate(member.user.last_access_at) }}
								</vs-tooltip>
								<div v-else>
									{{ toDate(member.user.last_access_at) }}
								</div>
							</vs-td>

							<!-- Colonne 4 : Actions (suppression utilisateur avec gestion admin) -->
							<vs-td>
								<s-button
									radius
									size="large"
									:type="`border-small${index === 0 ? '' : '-danger'}`"
									icon-pack="feather"
									icon="icon-delete"
									:class="{ 'cursor-not-allowed': index == 0 }"
									@click="index !== 0 && confirmDelAccess(member)"
								/>
							</vs-td>
						</Fragment>
					</vs-tr>
				</template>
			</vs-table>

			<!-- Invitations -->
			<template v-if="invites && invites.length > 0">
				<h4 class="mb-5 mt-10">{{ $t("Invitations en attente") }}</h4>

				<!-- On peut rajouter les attributs pagination & search si trop d'utilisateurs max-items="x" -->
				<vs-table v-if="invites" ref="tableInvite" class="text-left relative" stripe :data="invites" hover-flat>
					<template slot="thead">
						<vs-th />
						<vs-th>{{ $t("Nom") }}</vs-th>
						<vs-th>{{ $t("Email") }}</vs-th>
						<vs-th>{{ $t("Action") }}</vs-th>
						<vs-th v-if="(app?.admin_user_id === user.id && subscription) || user.is_superadmin">
							<vs-tooltip :text="$t('Lien invitation')" position="top" class="w-full">
								<div class="w-full flex justify-center items-center">Lien</div>
							</vs-tooltip>
						</vs-th>
					</template>

					<template slot-scope="{ data }">
						<vs-tr v-for="invite in data" :key="invite.id">
							<!-- Colonne 1 : photo avec gestion si admin -->
							<vs-td class="photoCol">
								<img
									:src="`https://sinao.app/services/avatar/${invite.firstname}${invite.lastname}/${invite.firstname[0]}${invite.lastname[0]}`"
									class="rounded-full img-profile"
									height="50px"
									width="50px"
									alt
								/>
							</vs-td>

							<!-- Colonne 2 : prénom + nom -->
							<vs-td>{{ invite.firstname }} {{ invite.lastname }}</vs-td>

							<!-- Colonne 3 : email -->
							<vs-td>{{ invite.email }}</vs-td>

							<!-- Colonne 4 : action -->
							<vs-td>
								<div class="flex">
									<vs-button
										class="ml-3"
										radius
										size="large"
										color="danger"
										type="border"
										icon-pack="feather"
										icon="icon-delete"
										@click="confirmDelInvite(invite)"
									/>
								</div>
							</vs-td>

							<!-- Colonne 5 : Invitation link -->
							<vs-td v-if="(app?.admin_user_id === user.id && subscription) || user.is_superadmin">
								<vs-tooltip :id="`tooltip${invite.id}`" :text="$t('Copier le lien')">
									<vs-button
										class="ml-3"
										radius
										size="large"
										type="flat"
										icon-pack="feather"
										color="dark"
										icon="icon-clipboard"
										@click="copyToClipboard(invite.token, invite.id)"
									/>
								</vs-tooltip>
							</vs-td>
						</vs-tr>
					</template>
				</vs-table>
			</template>

			<!-- acountants of the app -->
			<template v-if="accountants && accountants.length > 0">
				<h4 class="mb-5 mt-10">{{ $t("Cabinet comptable") }}</h4>
				<div class="text-sm pb-4" v-html="$t('Cabinet comptable explication')" />
				<vs-table ref="tableAccountants" class="text-left relative" stripe :data="accountants" hover-flat>
					<template slot="thead">
						<vs-th />
						<vs-th>{{ $t("Nom") }}</vs-th>
						<vs-th>{{ $t("Droits") }}</vs-th>
						<vs-th>{{ $t("Derniere Connexion") }}</vs-th>
						<vs-th></vs-th>
					</template>

					<template slot-scope="{ data }">
						<vs-tr v-for="(member, index) in data" :key="index">
							<!-- Colonne 1 : photo avec gestion si admin -->
							<Fragment v-if="member && member.user">
								<vs-td class="photoCol">
									<img
										:src="getImageUrl(member.user)"
										class="rounded-full img-profile"
										height="50px"
										width="50px"
										alt
									/>
								</vs-td>
								<!-- Colonne 2 : prénom + nom -->
								<vs-td>
									<div>
										{{ member.user.firstname }} {{ member.user.lastname }}
										<div style="font-size: 12px; color: var(--black)">{{ member.user.email }}</div>
									</div>
								</vs-td>

								<!-- Colonne 3 : Droits non cliquable -->
								<vs-td class="w-1/3">
									<div
										class="align-vertical text-link hover:title-sinao w-full pt-3 pb-3 no-underline text-base"
									>
										<p v-if="allPolicies && member.policy_profile_id">
											{{ $t(getPolicyById(member.policy_profile_id)?.name ?? "") }}
										</p>
									</div>
								</vs-td>

								<vs-td>
									<div>
										{{ toDate(member.user.last_access_at) }}
									</div>
								</vs-td>

								<!-- Colonne 4 : No Action -->
								<vs-td></vs-td>
							</Fragment>
						</vs-tr>
					</template>
				</vs-table>
			</template>

			<!-- Popup pour choisir les nouveaux policiesList -->
			<vs-popup
				v-if="selectedMember"
				:title="$t('Choisir les nouveaux droits d un membre')"
				:active.sync="showPoliciesPopup"
			>
				<!-- Div contenant logo + nom/prenom de l’utilisateur qu'on modifie -->
				<div class="flex flex-l items-center justify-center pbt-30">
					<img
						:src="getImageUrl(selectedMember.user)"
						class="rounded-full shadow-md mr-2 img-profile"
						height="50px"
						width="50px"
					/>
					<h4>
						{{ selectedMember.user.firstname }}
						{{ selectedMember.user.lastname }}
					</h4>
				</div>

				<!-- Div + table des policiesList pour selectionner -->
				<div class="vx-row">
					<vs-table
						v-if="allPolicies"
						v-model="selectedPolicy"
						:data="new_member_policies_list"
						hover-flat
						@selected="updatePolicy"
					>
						<template slot="thead">
							<vs-th class="w-1/3"> {{ $t("Nom") }} </vs-th>
							<vs-th>{{ $t("Description") }}</vs-th>
						</template>

						<template slot-scope="{ data }">
							<vs-tr
								v-for="(policy, index) in data"
								:key="index"
								:data="policy"
								class="cursor-pointer hover:bg-sinao hover:text-black hover:bg-border"
								style="text-align: left"
							>
								<vs-td class="w-1/3">
									{{ $t(policy.name) }}
								</vs-td>
								<vs-td>
									{{ $t("policy " + policy.name) }}
									<!-- {{ policy.description || "" }} -->
								</vs-td>
							</vs-tr>
						</template>
					</vs-table>
				</div>
			</vs-popup>
		</vx-card>
		<!-- invitation deleter component -->
		<invitation-deleter :selected-invite="selectedInvite" :app-id="parseInt(appId)" @done="confirmDelInviteDone" />
		<!-- user deleter component -->
		<policies-deleter
			:selected-member="getSelectedMember"
			:app-id="parseInt(appId)"
			type="user"
			@done="confirmDelAccessDone"
		/>
	</div>
</template>

<script>
	import { Fragment } from "vue-fragment";
	import { mapGetters, mapState } from "vuex";
	import i18n from "../i18n";
	import InvitationDeleter from "@/components/InvitationDeleter.vue";
	import PoliciesDeleter from "@/components/PoliciesDeleter.vue";

	export default {
		components: {
			PoliciesDeleter,
			InvitationDeleter,
			Fragment
		},
		data() {
			return {
				appId: parseInt(this.$route.params.appId),
				app: null,
				organization: null,
				policies: null,
				invites: null,
				members: null,
				supports: null,
				allPolicies: null,
				showPoliciesPopup: false,
				// permet d'appeler la fonction updatePolicy depuis la table dans popup
				selectedPolicy: null,
				selectedMember: null,
				selectedInvite: null,
				adminTooltip: {
					text: i18n.t(
						"Accès total et seule personne à voir et pouvoir gérer les pages Abonnement, Gérer les membres, Connexions API, Gérer les comptes connectés (Importation bancaire)."
					),
					title: i18n.t("Propriétaire de l’espace Sinao.")
				},
				subscription: null,
				deletableMember: null,
				accountant_policy_id: null,
				support_policy_id: null,
				accountants: null
			};
		},
		computed: {
			...mapGetters(["getImageUrl"]),
			...mapState(["user", "domain", "accountManagerUrl"]),
			new_member_policies_list() {
				return this.allPolicies.filter(p => p.visible === true);
			},
			getSelectedMember() {
				return this.deletableMember?.user ?? null;
			}
		},
		watch: {
			app() {
				if (this.app) {
					const { organization, policies, invites, admin, subscription } = this.app;

					this.organization = organization;
					//policies = policies;
					this.invites = invites.map(invite => Object.assign(invite, { visible: false }));
					this.subscription = subscription;
					this.members = policies.reduce((acc, cur) => {
						if (
							acc.findIndex(policy => policy.user_id === cur.user_id) === -1 &&
							cur.user_id !== admin.id &&
							cur.policy_profile_id !== this.accountant_policy_id &&
							cur.policy_profile_id !== this.support_policy_id
						) {
							acc.push(cur);
						}
						return acc;
					}, []);
					this.accountants = policies.reduce((acc, cur) => {
						if (
							acc.findIndex(policy => policy.user_id === cur.user_id) === -1 &&
							cur.user_id !== admin.id &&
							cur.policy_profile_id === this.accountant_policy_id
						) {
							acc.push(cur);
						}
						return acc;
					}, []);
					this.supports = policies.reduce((acc, cur) => {
						if (
							acc.findIndex(policy => policy.user_id === cur.user_id) === -1 &&
							cur.user_id !== admin.id &&
							cur.policy_profile_id === this.support_policy_id
						) {
							acc.push(cur);
						}
						return acc;
					}, []);

					this.members.unshift({
						user_id: admin.id,
						policy_profile_id: 6,
						app_id: this.appId,
						user: admin
					});
				}
			}
		},
		async created() {
			try {
				this.allPolicies = await this.$store.dispatch("getPolicies", this.appId);
				this.accountant_policy_id = this.getAccountantPolicyId();
				this.support_policy_id = this.getSupportPolicyId();
				this.app = await this.$store.dispatch("getAppByAppId", this.appId);
			} catch (error) {
				this.$router.push({ path: "/", query: this.$route.query });
			}
		},
		methods: {
			getAccountantPolicyId() {
				const p = this.allPolicies.find(policy => policy.name === "Comptable");
				return p?.id ?? null;
			},
			getSupportPolicyId() {
				const p = this.allPolicies.find(policy => policy.name === "Support technique");
				return p?.id ?? null;
			},
			getPolicyById(id) {
				if (this.allPolicies) {
					return this.allPolicies.find(policy => id === policy.id);
				}
			},
			// Fonction qui permet d'ouvrir la popup des policiesList et de passer l’utilisateur selectionné dans l'objet member
			openPoliciesPopup(member) {
				this.selectedMember = member;

				this.showPoliciesPopup = true;
			},
			// Appeler lors d’un clic selected dans la table popup des policiesList, permet de lancer une requete post modif access
			updatePolicy() {
				this.showPoliciesPopup = false;

				if (this.selectedMember && this.selectedPolicy) {
					const { user } = this.selectedMember;

					const payload = {
						appId: this.appId,
						userId: user.id,
						policyProfileId: this.selectedPolicy.id
					};

					this.$store
						.dispatch("updatePolicy", payload)
						.then(async () => {
							this.app = await this.$store.dispatch("getAppByAppId", this.appId);
						})
						.catch(() => {
							this.$vs.notify({
								title: i18n.t("Impossible de changer les droit de l’utilisateur"),
								text: `L’invitation de ${user.firstname} ${user.lastname} n’a pas pu être renouvelée. Raison : Application introuvable.`,
								color: "danger",
								iconPack: "feather",
								icon: "icon-alert-triangle",
								time: 15000
							});
						});
				}

				this.selectedMember = null;
				this.selectedPolicy = null;
			},
			// TODO : NOT USED, NEED TO BE IMPLEMENTED
			// supprime et recrée une invitation avec les meme valeur
			renewInvite(invite) {
				const payload = {
					appId: this.appId,
					email: invite.recipient_user.email,
					profile: invite.profile,
					firstname: invite.recipient_user.firstname,
					lastname: invite.recipient_user.lastname
				};

				this.$store
					.dispatch("sendInviteMail", payload)
					.then(() => {
						this.$vs.notify({
							title: i18n.t("Invitation renouvellée !"),
							text: `Un mail d’invitation a été renvoyé à ${this.inputPrenom} ${this.inputNom}.`,
							color: "success",
							iconPack: "feather",
							icon: "icon-check",
							time: 5000
						});
					})
					.catch(error => {
						this.$vs.notify({
							title: i18n.t("Impossible de renouveler l’invitation"),
							text: i18n.t("L’invitation de ... n’a pas pu être renouvelée.", {
								firstname: this.selectedMember?.user?.firstname,
								lastname: this.selectedMember?.user?.lastname,
								error: error
							}),
							color: "danger",
							iconPack: "feather",
							icon: "icon-alert-triangle",
							time: 15000
						});
					});

				this.selectedInvite = null;
			},
			// permet d'ouvrir un popup (dialog) pour confirmer la suppression d’un utilisateur
			confirmDelInvite(invite) {
				this.selectedInvite = invite; // component InvitationDeleter handle
			},
			// supprime une invitation
			async confirmDelInviteDone(confirm_value) {
				if (!confirm_value) {
					this.selectedInvite = null;
				} else {
					this.app = await this.$store.dispatch("getAppByAppId", this.appId);
				}
			},
			// permet d'ouvrir un popup (dialog) pour confirmer la suppression d’un utilisateur
			confirmDelAccess(member) {
				this.deletableMember = member; // component PoliciesDeleter handle
			},
			async confirmDelAccessDone(confirm_value) {
				if (!confirm_value) {
					this.deletableMember = null;
				} else {
					this.app = await this.$store.dispatch("getAppByAppId", this.appId);
				}
			},
			toDate: function (iso) {
				if (iso !== null) {
					const date = new Date(iso);
					const dateString = `${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}/${
						date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
					}/${date.getFullYear()}`;
					const hourString = `${date.getHours() < 10 ? `0${date.getHours()}` : date.getHours()}:${
						date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()
					}`;

					return `${dateString} à ${hourString}`;
				} else {
					return "--";
				}
			},
			// TODO : NOT USED, NEED TO BE IMPLEMENTED
			// permet de savoir si la date de validité en parametre est passée ou non
			isInviteOutOfDate: function (date) {
				date = new Date(date);

				return date.getTime() - Date.now() < 0;
			},
			copyToClipboard(token, refId) {
				const urlLink = `${this.accountManagerUrl}/invitation/${token}`;
				navigator.clipboard.writeText(urlLink).then(
					() => {
						/* clipboard successfully set */
						const index = this.invites.findIndex(invite => invite.id === refId);
						if (index >= 0) {
							const htmlElems = document.querySelectorAll(".vs-tooltip");
							const htmlElemsArray = [...htmlElems];
							htmlElemsArray.map(elem => {
								if (elem.textContent.trim() === i18n.t("Copier le lien")) {
									elem.textContent = i18n.t("Copié");
								}
								return elem;
							});
							setTimeout(() => {
								htmlElemsArray.map(elem => {
									if (elem.textContent.trim() === i18n.t("Copié")) {
										elem.textContent = i18n.t("Copier le lien");
										elem.style.display = "none";
									}
									return elem;
								});
							}, 1500);
						}
					},
					() => {
						/* clipboard write failed */
					}
				);
			}
		}
	};
</script>

<style>
	td.td-check,
	th.td-check {
		display: none;
	}

	div.vs-popup--background {
		background-color: rgb(0, 0, 0, 0.15);
	}

	div.vx-card__body,
	.paragraph {
		padding: 35px !important;
	}

	.addButton {
		margin-bottom: 35px !important;
	}

	.pbt-30 {
		padding-top: 30px;
		padding-bottom: 30px;
	}

	.con-vs-popup .vs-popup--content {
		padding-top: 0%;
	}

	.photoCol {
		width: 70px;
		text-align: -webkit-center;
	}
</style>
